import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function BlogArticleRow({ post, index }) {
  const isEven = index % 2 === 0;

  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-post-${index}`}
      style={{ textDecoration: "none", display: "block" }}
    >
      <article
        data-testid={`article-row-${index}`}
        className={`blog-row blog-row-${isEven ? "left" : "right"}`}
        style={{ cursor: "pointer" }}
      >
        {/* Image */}
        <div
          className={`blog-img-wrap ${isEven ? "blog-img-left" : "blog-img-right"}`}
          style={{ overflow: "hidden", position: "relative" }}
        >
          <img
            src={post.image_url}
            alt={post.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
            className="blog-img"
          />
        </div>

        {/* Text */}
        <div
          className={`blog-text-wrap ${isEven ? "blog-text-right" : "blog-text-left"}`}
          style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
        >
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "9px",
            letterSpacing: "4px",
            textTransform: "uppercase",
            color: "rgba(61,22,2,0.45)",
            marginBottom: "20px",
            fontWeight: "500",
          }}>
            {post.category}
          </p>

          <h2 className="blog-title" style={{
            fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
            fontSize: "clamp(1.5rem, 2.5vw, 2.1rem)",
            fontWeight: "300",
            color: "#3D1602",
            lineHeight: "1.25",
            letterSpacing: "-0.01em",
            marginBottom: "24px",
          }}>
            {post.title}
          </h2>

          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "13px",
            lineHeight: "1.85",
            color: "rgba(61,22,2,0.5)",
            fontWeight: "300",
            marginBottom: "20px",
            maxWidth: "360px",
          }}>
            {post.excerpt}
          </p>

          <span style={{
            fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
            fontSize: "18px",
            color: "rgba(61,22,2,0.25)",
          }}>
            —
          </span>
        </div>
      </article>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog`)
      .then(r => r.json())
      .then(data => { setPosts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <main data-testid="blog-page" style={{ paddingTop: "80px", backgroundColor: "#F7F3EA", minHeight: "100vh" }}>

      {/* En-tête */}
      <section style={{ padding: "80px 40px 72px", backgroundColor: "#F7F3EA" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "9px",
            letterSpacing: "5px",
            textTransform: "uppercase",
            color: "rgba(61,22,2,0.4)",
            marginBottom: "20px",
          }}>
            Le Carnet ELLAA
          </p>
          <h1 style={{
            fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
            fontSize: "clamp(2.4rem, 5vw, 4rem)",
            fontWeight: "300",
            color: "#3D1602",
            letterSpacing: "-0.02em",
            lineHeight: "1.1",
          }}>
            Conseils &amp; Inspirations
          </h1>
        </div>
      </section>

      {/* Séparateur */}
      <div style={{ maxWidth: "1200px", margin: "0 auto 0", padding: "0 40px" }}>
        <div style={{ height: "1px", backgroundColor: "rgba(61,22,2,0.08)" }} />
      </div>

      {/* Liste articles */}
      <section style={{ backgroundColor: "#F7F3EA", padding: "0 40px 120px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {loading ? (
            <div style={{ padding: "100px 0", textAlign: "center" }}>
              <p style={{
                fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
                fontStyle: "italic",
                fontSize: "20px",
                color: "rgba(61,22,2,0.4)",
              }}>
                Chargement...
              </p>
            </div>
          ) : posts.length === 0 ? (
            <div style={{ padding: "100px 0", textAlign: "center" }}>
              <p style={{ color: "rgba(61,22,2,0.4)", fontFamily: "'Inter', sans-serif", fontSize: "13px", letterSpacing: "2px" }}>
                Aucun article publié pour l'instant.
              </p>
            </div>
          ) : (
            posts.map((post, i) => (
              <div key={post.id || post.slug}>
                <BlogArticleRow post={post} index={i} />
                {i < posts.length - 1 && (
                  <div style={{ height: "1px", backgroundColor: "rgba(61,22,2,0.07)", margin: "0" }} />
                )}
              </div>
            ))
          )}
        </div>
      </section>

      <style>{`
        /* Base : toujours un grid */
        .blog-row {
          display: grid;
          grid-template-columns: 1fr;
        }
        /* Mobile : image en haut, texte en bas */
        .blog-img-left, .blog-img-right {
          height: 260px;
          order: 1;
          overflow: hidden;
          position: relative;
        }
        .blog-text-left, .blog-text-right {
          order: 2;
          padding: 32px 0 48px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        /* Desktop : layout zigzag 2 colonnes */
        @media (min-width: 768px) {
          .blog-row-left  { grid-template-columns: 45% 55%; min-height: 420px; }
          .blog-row-right { grid-template-columns: 55% 45%; min-height: 420px; }

          .blog-row-left  .blog-img-left  { grid-column: 1; grid-row: 1; height: 420px; order: unset; }
          .blog-row-left  .blog-text-right { grid-column: 2; grid-row: 1; padding: 60px 0 60px 72px; order: unset; }

          .blog-row-right .blog-text-left  { grid-column: 1; grid-row: 1; padding: 60px 72px 60px 0; order: unset; }
          .blog-row-right .blog-img-right  { grid-column: 2; grid-row: 1; height: 420px; order: unset; }
        }

        /* Hover zoom sur l'image */
        .blog-row:hover .blog-img { transform: scale(1.03); }
        .blog-row:hover .blog-title { opacity: 0.65; }
        .blog-img { transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1); }
        .blog-title { transition: opacity 0.3s ease; }
      `}</style>
    </main>
  );
}
